import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { getCurrentUser, type SessionUser, type SessionOrg } from '../services/auth';

export type SessionErrorKind = 'unauthenticated' | 'unreachable' | null;

interface SessionValue {
  user: SessionUser | null;
  org: SessionOrg | null;
  role: string | null;
  loading: boolean;
  error: SessionErrorKind;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | undefined>(undefined);

// Mirrors sk-catalog-frontend's src/context/sessionContext.tsx: the single
// source of truth for "who is logged in", sourced from the cached, cookie-
// authenticated GET /v1/me. There is no local login state to manage — the
// platform owns that — so this just reflects whatever the cookie session is.
export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [org, setOrg] = useState<SessionOrg | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<SessionErrorKind>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const me = await getCurrentUser();
      setUser(me?.user ?? null);
      setOrg(me?.org ?? null);
      setRole(me?.memberships?.[0]?.role ?? null);
      setError(null);
    } catch (err: any) {
      setUser(null);
      setOrg(null);
      setRole(null);
      // Distinguish "not logged in" (401/403 from the platform) from "the
      // platform is unreachable" (no response — DNS/CORS/down) so the root
      // gate only redirects on the former, avoiding a redirect loop when the
      // platform is merely slow or temporarily down.
      setError(err?.response ? 'unauthenticated' : 'unreachable');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <SessionContext.Provider value={{ user, org, role, loading, error, refresh }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within a SessionProvider');
  return ctx;
}
